// components/branches/config/appointment/CancellationPolicySettings.jsx
import React from "react";
import NumberInput from "../../../common/NumberInput";

const CancellationPolicySettings = ({ config, onChange, isEditMode }) => {
  const handleChange = (field, value) => {
    onChange({ ...config, [field]: value });
  };

  const handleNoShowPolicyChange = (field, value) => {
    onChange({
      ...config,
      noShowPolicy: {
        ...config.noShowPolicy,
        [field]: value,
      },
    });
  };

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold">Cancellation Policy</h3>
      <p className="text-gray-600">
        Define rules for appointment cancellations and no-shows
      </p>

      {/* Customer Can Cancel */}
      <div className="bg-gray-50 rounded-lg p-6">
        <div className="flex items-start justify-between">
          <div>
            <h4 className="font-medium">Allow Customer Cancellation</h4>
            <p className="text-sm text-gray-600 mt-1">
              Let customers cancel their own appointments
            </p>
          </div>
          {isEditMode ? (
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={config.customerCanCancel || false}
                onChange={(e) =>
                  handleChange("customerCanCancel", e.target.checked)
                }
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-blue-600 peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
            </label>
          ) : (
            <span
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                config.customerCanCancel
                  ? "bg-green-100 text-green-800"
                  : "bg-red-100 text-red-800"
              }`}
            >
              {config.customerCanCancel ? "Allowed" : "Not Allowed"}
            </span>
          )}
        </div>

        {config.customerCanCancel && (
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Minimum Hours Before Appointment
            </label>
            {isEditMode ? (
              <div className="flex items-center gap-2">
                <NumberInput
                  value={config.minHoursBeforeAppointment || 2}
                  onChange={(value) =>
                    handleChange("minHoursBeforeAppointment", value)
                  }
                  min="1"
                  max="48"
                  className="w-24 focus:ring-2 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-600">hours</span>
              </div>
            ) : (
              <p className="text-lg font-semibold">
                {config.minHoursBeforeAppointment || 2} hours
              </p>
            )}
          </div>
        )}
      </div>

      {/* Rescheduling */}
      <div className="bg-blue-50 rounded-lg p-6 space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <h4 className="font-medium">Allow Rescheduling</h4>
            <p className="text-sm text-gray-600 mt-1">
              Let customers reschedule appointments
            </p>
          </div>
          {isEditMode ? (
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={config.rescheduling?.allowed || false}
                onChange={(e) =>
                  onChange({
                    ...config,
                    rescheduling: {
                      ...config.rescheduling,
                      allowed: e.target.checked,
                    },
                  })
                }
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-blue-600 peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
            </label>
          ) : (
            <span
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                config.rescheduling?.allowed
                  ? "bg-green-100 text-green-800"
                  : "bg-gray-100 text-gray-800"
              }`}
            >
              {config.rescheduling?.allowed ? "Allowed" : "Not Allowed"}
            </span>
          )}
        </div>

        {config.rescheduling?.allowed && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Max Reschedules
              </label>
              {isEditMode ? (
                <NumberInput
                  value={config.rescheduling?.maxReschedules || 2}
                  onChange={(value) =>
                    onChange({
                      ...config,
                      rescheduling: {
                        ...config.rescheduling,
                        maxReschedules: value,
                      },
                    })
                  }
                  min="1"
                  max="5"
                  className="w-24 focus:ring-2 focus:ring-blue-500"
                />
              ) : (
                <p className="text-lg font-semibold">
                  {config.rescheduling?.maxReschedules || 2} times
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Min Hours Before
              </label>
              {isEditMode ? (
                <div className="flex items-center gap-2">
                  <NumberInput
                    value={config.rescheduling?.minHoursBefore || 4}
                    onChange={(value) =>
                      onChange({
                        ...config,
                        rescheduling: {
                          ...config.rescheduling,
                          minHoursBefore: value,
                        },
                      })
                    }
                    min="1"
                    max="48"
                    className="w-24 focus:ring-2 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-600">hours</span>
                </div>
              ) : (
                <p className="text-lg font-semibold">
                  {config.rescheduling?.minHoursBefore || 4} hours
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* No-Show Policy */}
      <div className="bg-red-50 rounded-lg p-6 space-y-4">
        <h4 className="font-medium">No-Show Policy</h4>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Maximum No-Shows Allowed
          </label>
          {isEditMode ? (
            <NumberInput
              value={config.noShowPolicy?.maxNoShows || 3}
              onChange={(value) =>
                handleNoShowPolicyChange("maxNoShows", value)
              }
              min="1"
              max="10"
              className="w-24 focus:ring-2 focus:ring-blue-500"
            />
          ) : (
            <p className="text-lg font-semibold">
              {config.noShowPolicy?.maxNoShows || 3} times
            </p>
          )}
          <p className="text-xs text-gray-500 mt-1">
            After this limit, temporary restrictions apply
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Restriction Period
          </label>
          {isEditMode ? (
            <div className="flex items-center gap-2">
              <NumberInput
                value={config.noShowPolicy?.restrictionDays || 30}
                onChange={(value) =>
                  handleNoShowPolicyChange("restrictionDays", value)
                }
                min="7"
                max="90"
                className="w-24 focus:ring-2 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-600">days</span>
            </div>
          ) : (
            <p className="text-lg font-semibold">
              {config.noShowPolicy?.restrictionDays || 30} days
            </p>
          )}
        </div>

        {isEditMode && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Penalty Type
            </label>
            <select
              value={config.noShowPolicy?.penaltyType || "PHONE_VERIFY_ONLY"}
              onChange={(e) =>
                handleNoShowPolicyChange("penaltyType", e.target.value)
              }
              className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="DEPOSIT_REQUIRED">Deposit Required</option>
              <option value="PHONE_VERIFY_ONLY">Phone Verification Only</option>
              <option value="TEMPORARY_BAN">Temporary Ban</option>
              <option value="NONE">No Penalty</option>
            </select>
          </div>
        )}
      </div>
    </div>
  );
};

export default CancellationPolicySettings;
